import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import {
  CreditCard, CheckCircle2, AlertTriangle, ShieldCheck, Banknote, LayoutGrid, Users, Activity,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * /clinician/eligibility — Blueprint Decision #22.
 *
 * Pre-visit payer + member ID + benefits check. Eliminates the
 * "visit happened, claim denied for ineligibility" failure mode.
 *
 * V1 returns a deterministic mock keyed to (payer, member_id) — same
 * input always yields the same answer, so clinicians can rehearse with
 * predictable scenarios. Live cutover is an env-var flip once the
 * Availity contract closes.
 */
export default function EligibilityVerify() {
  const { getAuthHeader } = useAuth();
  const [form, setForm] = useState({
    patient_id: '',
    payer: '',
    member_id: '',
    date_of_service: new Date().toISOString().slice(0, 10),
    cpt_code: '',
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.patient_id || !form.payer || !form.member_id) {
      toast.error('Patient ID, payer, and member ID are required.');
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/rcm/eligibility/verify`,
        form,
        getAuthHeader(),
      );
      setResult(data);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const navItems = [
    { icon: LayoutGrid, label: 'Eligibility', active: true },
    { icon: ShieldCheck, label: 'Trust Portal',  href: '/trust' },
    { icon: Activity,    label: 'Executive',     href: '/operations/executive' },
    { icon: Users,       label: 'Workforce',     href: '/agency/find-clinicians' },
    { icon: Banknote,    label: 'Revenue Cycle', href: '/clinician/billing' },
  ];

  return (
    <DashboardShell
      navItems={navItems}
      title="Eligibility Verification"
      subtitle="Pre-visit payer check — block bad claims before the visit happens."
      testId="eligibility-page"
    >
      <div className="grid lg:grid-cols-2 gap-6">
        {/* FORM */}
        <form onSubmit={submit} className="cn-card p-6 space-y-4" data-testid="eligibility-form">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="eligibility-patient">Patient ID</label>
            <input
              id="eligibility-patient"
              className="cn-input w-full"
              placeholder="patient_id (UUID)"
              value={form.patient_id}
              onChange={update('patient_id')}
              data-testid="eligibility-patient-id"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="eligibility-payer">Payer</label>
              <input
                id="eligibility-payer"
                className="cn-input w-full"
                placeholder="e.g. BlueCross of Virginia"
                value={form.payer}
                onChange={update('payer')}
                data-testid="eligibility-payer"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="eligibility-member">Member ID</label>
              <input
                id="eligibility-member"
                className="cn-input w-full"
                placeholder="e.g. A12345"
                value={form.member_id}
                onChange={update('member_id')}
                data-testid="eligibility-member-id"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="eligibility-dos">Date of service</label>
              <input
                id="eligibility-dos"
                type="date"
                className="cn-input w-full"
                value={form.date_of_service}
                onChange={update('date_of_service')}
                data-testid="eligibility-dos"
              />
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-cn-text-muted mb-1.5" htmlFor="eligibility-cpt">CPT (optional)</label>
              <input
                id="eligibility-cpt"
                className="cn-input w-full"
                placeholder="99213"
                value={form.cpt_code}
                onChange={update('cpt_code')}
                data-testid="eligibility-cpt"
              />
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="cn-btn cn-btn-primary w-full"
            data-testid="eligibility-submit"
          >
            <CreditCard className="w-4 h-4" />
            {loading ? 'Checking…' : 'Verify eligibility'}
          </Button>
          <p className="text-[11px] text-cn-text-muted leading-relaxed">
            Tip — to rehearse a rejection scenario, use a member ID starting with <code className="text-[10px]">X</code> or <code className="text-[10px]">Z</code>.
            To rehearse a prior-auth case, use any member ID ending in <code className="text-[10px]">9</code>.
          </p>
        </form>

        {/* RESULT */}
        <div data-testid="eligibility-result-panel">
          {!result ? (
            <div className="cn-card p-6 h-full flex flex-col items-center justify-center text-center min-h-[300px]">
              <CreditCard className="w-10 h-10 text-cn-text-muted/40 mb-3" />
              <p className="font-display font-semibold text-cn-text">No verification yet</p>
              <p className="text-sm text-cn-text-muted mt-1 max-w-xs">
                Submit a verification to see the payer&rsquo;s eligibility envelope.
              </p>
            </div>
          ) : (
            <div className="cn-card p-6 space-y-5">
              {/* Headline */}
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${
                      result.eligible
                        ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                        : 'bg-red-50 text-red-700 border-red-200'
                    }`}
                    data-testid="eligibility-status"
                  >
                    {result.eligible ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
                    {result.eligible ? 'Eligible' : 'Not eligible'}
                  </span>
                  <h3 className="mt-3 font-display font-bold text-xl text-cn-text dark:text-white">{result.plan_type || '—'}</h3>
                  <p className="text-xs text-cn-text-muted mt-1">{result.reason}</p>
                </div>
              </div>

              {/* Benefits grid */}
              {result.eligible && (
                <div className="grid grid-cols-2 gap-3" data-testid="eligibility-benefits">
                  <div className="cn-card !p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Copay</div>
                    <div className="font-display font-bold text-lg text-cn-text mt-0.5">{money(result.copay_cents)}</div>
                  </div>
                  <div className="cn-card !p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Deductible remaining</div>
                    <div className="font-display font-bold text-lg text-cn-text mt-0.5">{money(result.deductible_remaining_cents)}</div>
                  </div>
                  <div className="cn-card !p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Deductible total</div>
                    <div className="font-display font-bold text-lg text-cn-text mt-0.5">{money(result.deductible_total_cents)}</div>
                  </div>
                  <div className="cn-card !p-3">
                    <div className="text-[10px] uppercase tracking-wider font-bold text-cn-text-muted">Network</div>
                    <div className={`font-semibold text-sm mt-1 ${result.in_network ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {result.in_network ? 'In-network' : 'Out-of-network'}
                    </div>
                  </div>
                </div>
              )}

              {/* Prior auth */}
              {result.prior_auth_required && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex items-start gap-2" data-testid="eligibility-prior-auth">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
                  <p className="text-sm text-amber-800">Prior authorization required for this CPT. Submit pre-auth before the visit.</p>
                </div>
              )}

              {/* Warnings */}
              {result.warnings?.length > 0 && (
                <ul className="space-y-1.5" data-testid="eligibility-warnings">
                  {result.warnings.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-xs text-cn-text-muted">
                      <AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-amber-600 shrink-0" />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>
              )}

              {/* Live disclaimer */}
              <p className="text-[11px] text-cn-text-muted/80 italic border-t border-cn-border pt-3" data-testid="eligibility-disclaimer">
                {result.live_disclaimer}
              </p>
            </div>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}
